import React, { createContext, useContext, useState, useEffect } from 'react';

const CurrencyContext = createContext();

// Currency configuration
const CURRENCY_CONFIG = {
  USD: { symbol: '$', code: 'USD', name: 'US Dollar', rate: 1.0, locale: 'en-US' },
  GBP: { symbol: '£', code: 'GBP', name: 'British Pound', rate: 0.79, locale: 'en-GB' },
  EUR: { symbol: '€', code: 'EUR', name: 'Euro', rate: 0.92, locale: 'en-EU' },
  CAD: { symbol: 'C$', code: 'CAD', name: 'Canadian Dollar', rate: 1.36, locale: 'en-CA' },
  AUD: { symbol: 'A$', code: 'AUD', name: 'Australian Dollar', rate: 1.53, locale: 'en-AU' },
  INR: { symbol: '₹', code: 'INR', name: 'Indian Rupee', rate: 83.12, locale: 'en-IN' },
  ZAR: { symbol: 'R', code: 'ZAR', name: 'South African Rand', rate: 18.67, locale: 'en-ZA' },
  NGN: { symbol: '₦', code: 'NGN', name: 'Nigerian Naira', rate: 1505.50, locale: 'en-NG' },
};

// Country to currency mapping
const COUNTRY_CURRENCY_MAP = {
  US: 'USD', CA: 'CAD', GB: 'GBP', UK: 'GBP',
  FR: 'EUR', DE: 'EUR', ES: 'EUR', IT: 'EUR', NL: 'EUR', BE: 'EUR',
  AU: 'AUD', NZ: 'AUD',
  IN: 'INR',
  ZA: 'ZAR',
  NG: 'NGN',
};

// Base price in USD (cents)
const BASE_PRICE_USD_CENTS = 500; // $5.00

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState('GBP'); // Default to GBP as requested
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    detectCurrency();
  }, []);

  const detectCurrency = async () => {
    try {
      // Method 1: Try to get country from browser locale
      const browserLocale = navigator.language || navigator.userLanguage;
      const localeCountry = browserLocale.split('-')[1]?.toUpperCase();
      
      if (localeCountry && COUNTRY_CURRENCY_MAP[localeCountry]) {
        const detectedCurrency = COUNTRY_CURRENCY_MAP[localeCountry];
        setCurrency(detectedCurrency);
        setDetectedCountry(localeCountry);
        console.log('Currency detected from locale:', detectedCurrency, localeCountry);
        setIsLoading(false);
        return;
      }

      // Method 2: Try IP-based geolocation (using a free API)
      try {
        const response = await fetch('https://ipapi.co/json/', { timeout: 3000 });
        if (response.ok) {
          const data = await response.json();
          const countryCode = data.country_code;
          
          if (countryCode && COUNTRY_CURRENCY_MAP[countryCode]) {
            const detectedCurrency = COUNTRY_CURRENCY_MAP[countryCode];
            setCurrency(detectedCurrency);
            setDetectedCountry(countryCode);
            console.log('Currency detected from IP:', detectedCurrency, countryCode);
            setIsLoading(false);
            return;
          }
        }
      } catch (geoError) {
        console.log('Geolocation API failed, using default currency');
      }

      // Method 3: Check for UK-specific indicators
      const isUK = browserLocale.toLowerCase().includes('gb') || 
                   browserLocale.toLowerCase().includes('uk') ||
                   Intl.DateTimeFormat().resolvedOptions().timeZone?.includes('London');
      
      if (isUK) {
        setCurrency('GBP');
        setDetectedCountry('GB');
        console.log('UK detected, using GBP');
      }

      setIsLoading(false);
    } catch (error) {
      console.error('Currency detection failed:', error);
      // Default to GBP
      setCurrency('GBP');
      setIsLoading(false);
    }
  };

  const formatCurrency = (amountInCents, currencyCode = currency) => {
    const config = CURRENCY_CONFIG[currencyCode] || CURRENCY_CONFIG.GBP;
    const amount = amountInCents / 100;
    
    try {
      return new Intl.NumberFormat(config.locale, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      // Fallback formatting
      return `${config.symbol}${amount.toFixed(2)}`;
    }
  };

  const convertFromUSD = (amountInUsdCents) => {
    const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.GBP;
    return Math.round(amountInUsdCents * config.rate);
  };

  const convertToUSD = (amountInLocalCents) => {
    const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.GBP;
    return Math.round(amountInLocalCents / config.rate);
  };

  const getPriorityRequestPrice = () => {
    const priceInLocalCurrency = convertFromUSD(BASE_PRICE_USD_CENTS);
    return {
      cents: priceInLocalCurrency,
      formatted: formatCurrency(priceInLocalCurrency),
      currency: currency
    };
  };

  const getCurrencySymbol = (currencyCode = currency) => {
    const config = CURRENCY_CONFIG[currencyCode] || CURRENCY_CONFIG.GBP;
    return config.symbol;
  };

  const value = {
    currency,
    setCurrency,
    detectedCountry,
    isLoading,
    formatCurrency,
    convertFromUSD,
    convertToUSD,
    getPriorityRequestPrice,
    getCurrencySymbol,
    availableCurrencies: Object.keys(CURRENCY_CONFIG),
    currencyConfig: CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.GBP
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}